import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { Pagination } from '../../components/ui/Pagination';
import {
  badgeClass,
  dangerButtonClass,
  Field,
  FormActions,
  formatMoney,
  formatNumber,
  getApiErrorMessage,
  inputClass,
  inputErrorClass,
  isQuantity,
  primaryButtonClass,
  QUANTITY_STEP,
  secondaryButtonClass,
} from '../../components/ui/FormControls';
import { usePermissions } from '../../hooks/usePermissions';
import { cn } from '../../lib/utils';
import { defaultRowsPerPage } from '../../lib/preferences';

type Status = 'active' | 'expired' | 'fulfilled' | 'released';
type Kind = 'sales_order' | 'manual';

interface Reservation {
  id: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  kind: Kind;
  reference: string;
  quantity: number;
  issuedQuantity: number;
  openQuantity: number;
  status: Status;
  expiresOn: string | null;
  notes: string | null;
  releaseNote: string | null;
  createdAt: string;
}
interface ReservationPage {
  data: Reservation[];
  total: number;
  totalPages: number;
}
interface Summary {
  activeReservations: number;
  productsReserved: number;
  reservedValue: number;
  expiredReservations: number;
  productsShort: number;
}
interface Availability {
  onHand: number;
  held: number;
  reserved: number;
  free: number;
  shortfall: number;
}
interface OrderLine {
  salesOrderItemId: string;
  productId: string;
  productName: string;
  sku: string;
  unit: string;
  quantity: number;
  dispatchedQuantity: number;
  toGoQuantity: number;
  reservedQuantity: number;
  unreservedQuantity: number;
  freeQuantity: number;
}
interface OrderLines {
  salesOrderId: string;
  orderNumber: string;
  customerName: string | null;
  status: string;
  canReserve: boolean;
  reason: string | null;
  lines: OrderLine[];
  reservedLines?: number;
}
interface ProductChoice {
  id: string;
  name: string;
  sku: string;
  unit: string;
  currentStock: number;
}

const PAGE_SIZES = [10, 25, 50, 100];
const thClass = 'px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500';
const STATUS: Record<Status, { label: string; className: string }> = {
  active: { label: 'Holding stock', className: 'bg-blue-50 text-blue-700 ring-blue-600/20' },
  expired: { label: 'Lapsed', className: 'bg-amber-50 text-amber-800 ring-amber-600/20' },
  fulfilled: { label: 'Dispatched', className: 'bg-green-50 text-green-700 ring-green-600/20' },
  released: { label: 'Released', className: 'bg-gray-50 text-gray-600 ring-gray-500/20' },
};
const KINDS: Record<Kind, string> = { sales_order: 'Sales order', manual: 'Kept aside' };

const today = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
};
const formatDate = (value?: string | null) =>
  value ? new Date(String(value).length <= 10 ? `${value}T00:00:00` : value).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const clean = (params: object) => Object.fromEntries(Object.entries(params).filter(([, value]) => value !== undefined && value !== ''));

const reservationApi = {
  page: async (params: object): Promise<ReservationPage> => (await api.get('/stock-reservations', { params: clean(params) })).data,
  summary: async (): Promise<Summary> => (await api.get('/stock-reservations/summary')).data,
  availability: async (productId: string): Promise<Availability> => (await api.get(`/stock-reservations/availability/${productId}`)).data,
  orderLines: async (salesOrderId: string): Promise<OrderLines> => (await api.get(`/stock-reservations/sales-orders/${salesOrderId}`)).data,
  create: async (data: object): Promise<Reservation> => (await api.post('/stock-reservations', data)).data,
  reserveOrder: async (salesOrderId: string): Promise<OrderLines> => (await api.post(`/stock-reservations/sales-orders/${salesOrderId}`, {})).data,
  release: async (id: string, note: string): Promise<Reservation> => (await api.post(`/stock-reservations/${id}/release`, { note: note || null })).data,
  products: async (search: string): Promise<ProductChoice[]> => (await api.get('/inventory/paginated', { params: { search, limit: 8, page: 1 } })).data.data,
};

// ---------------------------------------------------------------- keep stock aside by hand

const ManualForm: React.FC<{ isPending: boolean; onCancel: () => void; onSubmit: (data: object) => void }> = ({ isPending, onCancel, onSubmit }) => {
  const [form, setForm] = useState({ quantity: '', reference: '', expiresOn: '', notes: '' });
  const [product, setProduct] = useState<ProductChoice | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: choices = [], isFetching } = useQuery({
    queryKey: ['reservations', 'products', search],
    queryFn: () => reservationApi.products(search),
    enabled: !product && search.length >= 2,
  });
  const { data: availability } = useQuery({
    queryKey: ['reservations', 'availability', product?.id],
    queryFn: () => reservationApi.availability(product!.id),
    enabled: !!product,
  });

  const set = (field: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm((prev) => ({ ...prev, [field]: event.target.value }));
    setErrors((prev) => {
      const { [field]: _cleared, ...rest } = prev;
      return rest;
    });
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const quantity = Number(form.quantity);
    const next: Record<string, string> = {};
    if (!product) next.product = 'Choose the product';
    if (form.quantity.trim() === '' || !isQuantity(quantity) || quantity <= 0) next.quantity = 'Enter a quantity above zero, up to 4 decimals';
    else if (availability && quantity > availability.free) next.quantity = `Only ${formatNumber(availability.free)} ${product?.unit ?? ''} is free`;
    if (form.reference.trim().length < 3) next.reference = 'Say what the stock is kept for';
    if (form.expiresOn && form.expiresOn < today()) next.expiresOn = 'Not in the past';
    setErrors(next);
    if (Object.keys(next).length > 0 || !product) return;
    onSubmit({ productId: product.id, quantity, reference: form.reference.trim(), expiresOn: form.expiresOn || null, notes: form.notes.trim() || null });
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="space-y-4">
      <Field label="Product" htmlFor="rs-product" required error={errors.product}>
        {product ? (
          <div className="flex items-center justify-between gap-3 rounded-md bg-gray-50 px-3 py-2 ring-1 ring-gray-200">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium text-gray-900">{product.name}</p>
              <p className="text-xs text-gray-500">
                {product.sku}
                {availability &&
                  ` · ${formatNumber(availability.onHand)} ${product.unit} on hand · ${formatNumber(availability.reserved)} reserved${availability.held > 0 ? ` · ${formatNumber(availability.held)} held for inspection` : ''} · ${formatNumber(availability.free)} free`}
              </p>
            </div>
            <button type="button" onClick={() => setProduct(null)} className="shrink-0 text-xs font-semibold text-indigo-600 hover:text-indigo-500">
              Change
            </button>
          </div>
        ) : (
          <div>
            <div className="relative">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input id="rs-product" type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search product or SKU" className={cn(inputClass, 'pl-9', errors.product && inputErrorClass)} />
            </div>
            {search.length >= 2 && (
              <ul className="mt-1 max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-md border border-gray-200">
                {choices.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-gray-500">{isFetching ? 'Searching…' : 'No product matches'}</li>
                ) : (
                  choices.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        onClick={() => {
                          setProduct(row);
                          setErrors((prev) => {
                            const { product: _cleared, ...rest } = prev;
                            return rest;
                          });
                        }}
                        className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-indigo-50"
                      >
                        <span className="min-w-0 truncate">
                          <span className="font-medium text-gray-900">{row.name}</span> <span className="text-gray-500">{row.sku}</span>
                        </span>
                        <span className="shrink-0 text-gray-600 tabular-nums">
                          {formatNumber(Number(row.currentStock))} {row.unit}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            )}
          </div>
        )}
      </Field>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Quantity" htmlFor="rs-quantity" required error={errors.quantity}>
          <input id="rs-quantity" type="number" min="0" step={QUANTITY_STEP} value={form.quantity} onChange={set('quantity')} className={cn(inputClass, 'text-right tabular-nums', errors.quantity && inputErrorClass)} />
        </Field>
        <Field label="Last day" htmlFor="rs-expires" error={errors.expiresOn} hint="Optional. After this day the stock is free again.">
          <input id="rs-expires" type="date" min={today()} value={form.expiresOn} onChange={set('expiresOn')} className={cn(inputClass, errors.expiresOn && inputErrorClass)} />
        </Field>
        <Field label="Kept for" htmlFor="rs-reference" required error={errors.reference} className="sm:col-span-2">
          <input id="rs-reference" type="text" maxLength={200} value={form.reference} onChange={set('reference')} className={cn(inputClass, errors.reference && inputErrorClass)} placeholder="Exhibition samples, a customer who is about to order…" />
        </Field>
        <Field label="Notes" htmlFor="rs-notes" className="sm:col-span-2">
          <input id="rs-notes" type="text" maxLength={300} value={form.notes} onChange={set('notes')} className={inputClass} />
        </Field>
      </div>
      <p className="text-xs text-gray-500">The stock stays in the warehouse and in the stock figure. It cannot be dispatched, consumed or scrapped until the reservation is released.</p>
      <FormActions>
        <button type="button" onClick={onCancel} className={secondaryButtonClass}>
          Cancel
        </button>
        <button type="submit" disabled={isPending} className={primaryButtonClass}>
          {isPending ? 'Saving…' : 'Reserve Stock'}
        </button>
      </FormActions>
    </form>
  );
};

// ---------------------------------------------------------------- reserve for a sales order

const OrderPanel: React.FC<{ salesOrderId: string; canReserve: boolean; isPending: boolean; onClose: () => void; onReserve: () => void }> = ({ salesOrderId, canReserve, isPending, onClose, onReserve }) => {
  const { data, isLoading, isError, error } = useQuery({ queryKey: ['reservations', 'order', salesOrderId], queryFn: () => reservationApi.orderLines(salesOrderId) });
  if (isLoading) return <p className="py-10 text-center text-sm text-gray-500">Loading…</p>;
  if (isError || !data) return <p className="py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The sales order could not be loaded.')}</p>;

  // Free stock is shared by the lines of the same product, so it is handed out line by line
  const left = new Map<string, number>();
  const possible = data.lines.reduce((sum, line) => {
    const free = left.has(line.productId) ? left.get(line.productId)! : line.freeQuantity;
    const take = Math.min(line.unreservedQuantity, free);
    left.set(line.productId, free - take);
    return sum + take;
  }, 0);
  const nothingToReserve = data.lines.every((line) => line.unreservedQuantity === 0);

  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-600">
        {data.customerName ?? 'Customer'} · {data.status.replace('_', ' ')}
      </p>
      {data.reason && <p className="rounded-md bg-amber-50 px-4 py-3 text-sm text-amber-900 ring-1 ring-amber-200">{data.reason}</p>}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className={thClass}>Product</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Still to go</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Reserved</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Not reserved</th>
              <th scope="col" className={cn(thClass, 'text-right')}>Free stock</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {data.lines.map((line) => (
              <tr key={line.salesOrderItemId}>
                <td className="px-4 py-2.5">
                  <p className="text-sm font-medium text-gray-900">{line.productName}</p>
                  <p className="text-xs text-gray-500">
                    {line.sku} · ordered {formatNumber(line.quantity)} {line.unit}
                  </p>
                </td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm text-gray-700 tabular-nums">{formatNumber(line.toGoQuantity)}</td>
                <td className="whitespace-nowrap px-4 py-2.5 text-right text-sm font-medium text-gray-900 tabular-nums">{formatNumber(line.reservedQuantity)}</td>
                <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', line.unreservedQuantity > 0 ? 'font-medium text-amber-700' : 'text-gray-400')}>
                  {line.unreservedQuantity > 0 ? formatNumber(line.unreservedQuantity) : '—'}
                </td>
                <td className={cn('whitespace-nowrap px-4 py-2.5 text-right text-sm tabular-nums', line.unreservedQuantity > line.freeQuantity ? 'font-medium text-red-600' : 'text-gray-700')}>{formatNumber(line.freeQuantity)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {data.canReserve && (
        <p className="text-xs text-gray-500">
          {nothingToReserve
            ? 'Everything still to go on this order is reserved.'
            : possible > 0
              ? 'Reserving takes what is free for each line. Where the free stock is short, the line is reserved in part and can be topped up later.'
              : 'No free stock is left for the lines that are not reserved.'}
        </p>
      )}
      <FormActions>
        <button type="button" onClick={onClose} className={secondaryButtonClass}>
          Close
        </button>
        {data.canReserve && canReserve && (
          <button type="button" onClick={onReserve} disabled={isPending || possible <= 0} className={primaryButtonClass}>
            {isPending ? 'Reserving…' : 'Reserve What Is Free'}
          </button>
        )}
      </FormActions>
    </div>
  );
};

// ---------------------------------------------------------------- the page

const Reservations: React.FC = () => {
  const { can } = usePermissions();
  const canCreate = can('inventory.create');
  const canRelease = can('inventory.update');
  const queryClient = useQueryClient();

  const [searchParams, setSearchParams] = useSearchParams();
  const orderFromLink = searchParams.get('so') ?? '';

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultRowsPerPage());
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<Status | ''>('active');
  const [kindFilter, setKindFilter] = useState<Kind | ''>('');
  const [formOpen, setFormOpen] = useState(false);
  const [releasing, setReleasing] = useState<Reservation | null>(null);
  const [releaseNote, setReleaseNote] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, isError, error, isFetching } = useQuery({
    queryKey: ['reservations', 'page', { page, limit, search, status: statusFilter, kind: kindFilter }],
    queryFn: () => reservationApi.page({ page, limit, search, status: statusFilter, kind: kindFilter }),
    placeholderData: keepPreviousData,
  });
  const { data: summary } = useQuery({ queryKey: ['reservations', 'summary'], queryFn: reservationApi.summary });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['reservations'] });
  const closeOrder = () => setSearchParams({}, { replace: true });
  const closeRelease = () => {
    setReleasing(null);
    setReleaseNote('');
  };

  const createManual = useMutation({
    mutationFn: reservationApi.create,
    onSuccess: (reservation) => {
      toast.success(`${formatNumber(reservation.quantity)} ${reservation.unit} of ${reservation.productName} reserved`);
      refresh();
      setFormOpen(false);
    },
    onError: (submitError) => toast.error(getApiErrorMessage(submitError, 'The stock could not be reserved')),
  });
  const reserveOrder = useMutation({
    mutationFn: reservationApi.reserveOrder,
    onSuccess: (result) => {
      const short = result.lines.filter((line) => line.unreservedQuantity > 0).length;
      toast.success(short > 0 ? `Reserved for ${result.orderNumber}; ${short} line(s) are still short` : `Stock reserved for ${result.orderNumber}`);
      refresh();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The stock could not be reserved')),
  });
  const release = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => reservationApi.release(id, note),
    onSuccess: () => {
      toast.success('Reservation released; the stock is free');
      refresh();
      closeRelease();
    },
    onError: (actionError) => toast.error(getApiErrorMessage(actionError, 'The reservation could not be released')),
  });

  const rows = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 1;
  const hasFilters = !!search || !!kindFilter || statusFilter !== 'active';
  const firstRow = total > 0 ? (page - 1) * limit + 1 : 0;
  const lastRow = Math.min(page * limit, total);

  const stats = [
    { label: 'Holding stock', value: formatNumber(summary?.activeReservations ?? 0), hint: `Reservations, on ${formatNumber(summary?.productsReserved ?? 0)} product(s)` },
    { label: 'Value reserved', value: formatMoney(summary?.reservedValue ?? 0), hint: 'At cost price' },
    { label: 'Lapsed', value: formatNumber(summary?.expiredReservations ?? 0), hint: 'Past their last day; they hold nothing' },
    { label: 'Not covered', value: formatNumber(summary?.productsShort ?? 0), hint: 'Products with more reserved than is in stock' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Stock Reservations</h1>
          <p className="mt-1 text-sm text-gray-500">Stock that is spoken for: for the lines of confirmed sales orders, or kept aside by hand. Reserved stock is not issued to anything else.</p>
        </div>
        {canCreate && (
          <button type="button" onClick={() => setFormOpen(true)} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Keep Stock Aside
          </button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.label} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-gray-200">
            <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{stat.label}</p>
            <p className="mt-1 text-2xl font-semibold text-gray-900">{stat.value}</p>
            <p className="mt-1 text-xs text-gray-500">{stat.hint}</p>
          </div>
        ))}
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
        <div className="flex flex-col gap-3 border-b border-gray-200 p-4 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by product, SKU, order number or purpose" aria-label="Search reservations" className={cn(inputClass, 'pl-9')} />
          </div>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as Status | ''); setPage(1); }} aria-label="Status" className={cn(inputClass, 'lg:w-44')}>
            <option value="">All statuses</option>
            {(Object.keys(STATUS) as Status[]).map((status) => (<option key={status} value={status}>{STATUS[status].label}</option>))}
          </select>
          <select value={kindFilter} onChange={(event) => { setKindFilter(event.target.value as Kind | ''); setPage(1); }} aria-label="Kind" className={cn(inputClass, 'lg:w-44')}>
            <option value="">All kinds</option>
            {(Object.keys(KINDS) as Kind[]).map((kind) => (<option key={kind} value={kind}>{KINDS[kind]}</option>))}
          </select>
        </div>

        <div className={cn('overflow-x-auto', isFetching && data && 'opacity-60')}>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className={thClass}>Product</th>
                <th scope="col" className={thClass}>Reserved for</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Reserved</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Dispatched</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Still held</th>
                <th scope="col" className={thClass}>Status</th>
                <th scope="col" className={cn(thClass, 'text-right')}>Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isError ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-700">{getApiErrorMessage(error, 'The reservations could not be loaded.')}</td></tr>
              ) : isLoading ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-sm text-gray-500">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center">
                    <p className="text-sm font-medium text-gray-900">{hasFilters ? 'No reservation matches' : 'No stock is reserved'}</p>
                    <p className="mt-1 text-sm text-gray-500">Reserve stock for a confirmed sales order from the Sales list, or keep stock aside here.</p>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="hover:bg-gray-50">
                    <td className="max-w-xs px-4 py-3">
                      <p className="truncate text-sm font-medium text-gray-900">{row.productName}</p>
                      <p className="mt-0.5 text-xs text-gray-500">{row.sku}</p>
                    </td>
                    <td className="max-w-sm px-4 py-3">
                      <p className="truncate text-sm text-gray-900" title={row.reference}>{row.reference}</p>
                      <p className="mt-0.5 truncate text-xs text-gray-500">
                        {KINDS[row.kind]} · {formatDate(row.createdAt)}
                        {row.expiresOn ? ` · until ${formatDate(row.expiresOn)}` : ''}
                        {row.releaseNote ? ` · ${row.releaseNote}` : ''}
                      </p>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">
                      {formatNumber(row.quantity)} <span className="text-gray-500">{row.unit}</span>
                    </td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm text-gray-700 tabular-nums">{row.issuedQuantity > 0 ? formatNumber(row.issuedQuantity) : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right text-sm font-medium text-gray-900 tabular-nums">{row.status === 'active' ? formatNumber(row.openQuantity) : '—'}</td>
                    <td className="whitespace-nowrap px-4 py-3"><span className={cn(badgeClass, STATUS[row.status].className)}>{STATUS[row.status].label}</span></td>
                    <td className="whitespace-nowrap px-4 py-3 text-right">
                      {(row.status === 'active' || row.status === 'expired') && canRelease ? (
                        <button type="button" onClick={() => setReleasing(row)} className="rounded-md px-2.5 py-1 text-xs font-semibold text-red-600 ring-1 ring-inset ring-red-200 hover:bg-red-50" aria-label={`Release the reservation of ${row.productName} for ${row.reference}`}>
                          Release
                        </button>
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-3 border-t border-gray-200 px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">
            <span>Showing {firstRow}–{lastRow} of {formatNumber(total)}</span>
            <label className="flex items-center gap-2">
              Rows per page
              <select value={limit} onChange={(event) => { setLimit(Number(event.target.value)); setPage(1); }} className="rounded-md border border-gray-300 bg-white py-1 pl-2 pr-7 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                {PAGE_SIZES.map((size) => (<option key={size} value={size}>{size}</option>))}
              </select>
            </label>
          </div>
          {totalPages > 1 && <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />}
        </div>
      </div>

      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title="Keep Stock Aside" description="For a purpose that is not a sales order yet." size="lg">
        {formOpen && <ManualForm isPending={createManual.isPending} onCancel={() => setFormOpen(false)} onSubmit={(input) => createManual.mutate(input)} />}
      </Modal>

      <Modal isOpen={!!orderFromLink && !formOpen} onClose={closeOrder} title="Reserve Stock for a Sales Order" size="xl">
        {orderFromLink && <OrderPanel salesOrderId={orderFromLink} canReserve={canCreate} isPending={reserveOrder.isPending} onClose={closeOrder} onReserve={() => reserveOrder.mutate(orderFromLink)} />}
      </Modal>

      <Modal isOpen={!!releasing} onClose={closeRelease} title="Release Reservation" size="md">
        {releasing && (
          <form
            noValidate
            onSubmit={(event) => {
              event.preventDefault();
              release.mutate({ id: releasing.id, note: releaseNote.trim() });
            }}
            className="space-y-4"
          >
            <p className="text-sm text-gray-700">
              {formatNumber(releasing.openQuantity)} {releasing.unit} of {releasing.productName}, reserved for {releasing.reference}, become free stock again.
            </p>
            <Field label="Why is it released?" htmlFor="rs-release-note" hint="Optional">
              <input id="rs-release-note" type="text" maxLength={300} value={releaseNote} onChange={(event) => setReleaseNote(event.target.value)} className={inputClass} />
            </Field>
            <FormActions>
              <button type="button" onClick={closeRelease} className={secondaryButtonClass}>
                Keep It
              </button>
              <button type="submit" disabled={release.isPending} className={dangerButtonClass}>
                {release.isPending ? 'Releasing…' : 'Release Reservation'}
              </button>
            </FormActions>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Reservations;
